import { useState, useCallback, useRef, useEffect, Fragment, useMemo, isValidElement, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Project, ReviewCheckType, UpdateProjectData } from '@/api/client';
import {
  ReaderPriceList,
  projectReadersToEntries,
  entriesToReaderInputs,
  isValidReaderTimeInput,
  type ReaderEntryItem,
} from '@/components/ReaderPriceList/ReaderPriceList';
import type { AuthorValueItem } from '@/components/AuthorCombo/AuthorCombo';
import {
  ContractorServiceList,
  type ContractorEntryItem,
  projectContractorsToEntries,
  entriesToInputs,
} from '@/components/ContractorServiceList/ContractorServiceList';
import type { ProducerValueItem } from '@/components/ProducerCombo/ProducerCombo';
import type { TecheditorValueItem } from '@/components/TecheditorCombo/TecheditorCombo';
import { CorrectorCombo, type CorrectorValueItem } from '@/components/CorrectorCombo/CorrectorCombo';
import { format, isValid, parse } from 'date-fns';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { DateAutofillInput } from '@/components/ui/DateAutofillInput/DateAutofillInput';
import { PaperBookPlanBacklogCombo } from '@/components/ui/PaperBookPlanBacklogCombo/PaperBookPlanBacklogCombo';
import { TimeInput } from '@/components/ui/TimeInput/TimeInput';
import { AuthorCombo } from '@/components/AuthorCombo/AuthorCombo';
import { ProducerCombo } from '@/components/ProducerCombo/ProducerCombo';
import { TecheditorCombo } from '@/components/TecheditorCombo/TecheditorCombo';
import { DepartmentCombo } from '@/components/DepartmentCombo/DepartmentCombo';
import { EditionCombo } from '@/components/EditionCombo/EditionCombo';
import { EditorCombo } from '@/components/EditorCombo/EditorCombo';
import type { DepartmentValueItem } from '@/components/DepartmentCombo/DepartmentCombo';
import type { EditionValueItem } from '@/components/EditionCombo/EditionCombo';
import type { EditorValueItem } from '@/components/EditorCombo/EditorCombo';
import { Button } from '@/components/ui/Button/Button';
import { BookmarkIcon, InfoIcon } from '@/components/ui/Icons';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { Switch } from '@/components/ui/Switch/Switch';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { HistoryIcon } from '@/components/ui/Icons';
import { ProjectChangeHistoryPopover } from '@/features/project-change-history/ProjectChangeHistoryPopover';
import {
  resolveHistoryColumnId,
  resolveHistoryColumnIdsForCombo,
} from '@/features/project-change-history/resolveHistoryColumnId';
import { invalidateProjectHistory } from '@/features/project-change-history/projectChangeHistoryQuery';
import {
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';
import { useProjectRowOpenDetails } from './ProjectRowProjectDetailsContext';
import {
  REVIEW_CHECK_TYPE_LABELS,
  REVIEW_CHECK_TYPES,
  REVIEW_CHECK_TYPE_PLACEHOLDER,
  REVIEW_NOT_SPECIFIED_LABEL,
} from '@/utils/reviewCheckType';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useEditionEditorDepartmentHandlers } from '@/hooks/useEditionEditorDepartmentHandlers';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { ContractorsCell } from './ContractorsCell';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { Badge } from '@/components/ui/Badge/Badge';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { omitPriceKeys } from '@/lib/omitPriceKeys';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  captureProjectListRollback,
  patchProjectListCaches,
  type ProjectListRollbackContext,
} from '@/lib/projectListQueryCache';
import { PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY } from '@/lib/projectEntityCache';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import {
  MAX_NAME_NOTE_LEN,
  NAME_NOTE_TEXTAREA_MAX_ROWS,
  nameNoteSingleLine,
} from '@/utils/nameNote';
import './EditableCell.css';
import { EditableCellPopoverHeader } from './EditableCellPopoverHeader';

function reactElementTypeDisplayName(type: unknown): string | undefined {
  if (typeof type === 'function' || (typeof type === 'object' && type !== null)) {
    const t = type as { displayName?: string; name?: string };
    if (typeof t.displayName === 'string') return t.displayName;
    if (typeof t.name === 'string') return t.name;
  }
  return undefined;
}

function isContractorsCellLike(el: unknown): el is React.ReactElement {
  if (!isValidElement(el)) return false;
  if (el.type === ContractorsCell) return true;
  return reactElementTypeDisplayName(el.type) === 'ContractorsCell';
}

function isNameNoteOnlyPatch(data: UpdateProjectData | undefined): boolean {
  if (!data) return false;
  const keys = Object.keys(data);
  return keys.length === 1 && keys[0] === 'name_note';
}

type EditableCellOptimistic = {
  authors?: { id: string; name: string }[];
  readers?: ReaderEntryItem[];
  contractorEntries?: ContractorEntryItem[];
  producers?: { id: string; name: string }[];
  techeditors?: { id: string; name: string }[];
  correctors?: { id: string; name: string }[];
  edition?: { edition_name: string; edition_department_id?: string | null; edition_department_code?: string | null };
  editor?: { editor_name: string; edition_name?: string | null; edition_department_id?: string | null; edition_department_code?: string | null };
};

function applyOptimisticProjectPatch(
  prev: Project[] | undefined,
  id: string,
  data: UpdateProjectData,
  optimistic?: EditableCellOptimistic
): Project[] | undefined {
  if (!prev) return prev;
  return prev.map((p) => {
    if (p.id !== id) return p;
    const updated = { ...p };
    if (data.name !== undefined) updated.name = data.name;
    if (data.name_note !== undefined) updated.name_note = data.name_note;
    if (data.pages !== undefined) updated.pages = data.pages;
    if (data.deadline !== undefined) updated.deadline = data.deadline;
    if (data.review_deadline !== undefined) updated.review_deadline = data.review_deadline;
    if (data.review_check_type !== undefined) updated.review_check_type = data.review_check_type;
    if (data.no_edits !== undefined) updated.no_edits = data.no_edits;
    if (data.corrections_number !== undefined) updated.corrections_number = data.corrections_number;
    if (data.author_sheets !== undefined) updated.author_sheets = data.author_sheets;
    if (data.tracks_count !== undefined) updated.tracks_count = data.tracks_count;
    if (data.size !== undefined) updated.size = data.size;
    if (data.time !== undefined) updated.time = data.time;
    if (data.asta !== undefined) updated.asta = data.asta;
    if (data.ase !== undefined) updated.ase = data.ase;
    if (data.rin !== undefined) updated.rin = data.rin;
    if (data.paper_book_plan !== undefined) updated.paper_book_plan = data.paper_book_plan;
    if (data.is_backlog !== undefined) updated.is_backlog = data.is_backlog;
    if (data.is_project_highlighted !== undefined) updated.is_project_highlighted = data.is_project_highlighted;
    if (data.author_ids !== undefined && optimistic?.authors) {
      updated.authors = optimistic.authors.map((a) => ({ id: a.id, name: a.name }));
    }
    if ((data.reader_ids !== undefined || data.reader_entries !== undefined) && optimistic?.readers) {
      const inputs = entriesToReaderInputs(optimistic.readers);
      updated.readers = optimistic.readers
        .filter((r) => r.id)
        .map((r) => {
          const prevReader = p.readers?.find((pr) => pr.id === r.id);
          const inp = inputs.find((x) => x.person_id === r.id);
          return {
            ...(prevReader ?? { id: r.id, name: r.name }),
            id: r.id,
            name: r.name,
            reader_time: inp?.reader_time ?? null,
            reader_custom_price: inp?.reader_custom_price ?? null,
            is_reader_price_fixed: inp?.is_reader_price_fixed ?? false,
            reader_catalog_price_id: inp?.reader_catalog_price_id ?? null,
            reader_edits_number: inp?.reader_edits_number ?? null,
          };
        });
    }
    if ((data.project_contracts !== undefined || data.contractor_entries !== undefined) && optimistic?.contractorEntries) {
      updated.contractors = optimistic.contractorEntries
        .filter((e) => e.contractor_id)
        .map((e) => ({
          id: e.contractor_id,
          project_contractor_id: e.project_contractor_id ?? undefined,
          contractor_id: e.contractor_id,
          name: e.contractor_name,
          service_id: e.service_id,
          service_name: e.service_name,
          service_price: e.service_price,
          service_custom_price: e.price_input ? parseFloat(e.price_input) : null,
          is_service_price_fixed: e.is_price_fixed,
          is_contractor_included_in_budget: e.is_contractor_included_in_budget === true,
        }));
    }
    if (data.producer_ids !== undefined && optimistic?.producers) {
      updated.producers = optimistic.producers.map((pr) => ({ id: pr.id, name: pr.name }));
    }
    if (data.techeditor_ids !== undefined && optimistic?.techeditors) {
      updated.techeditors = optimistic.techeditors.map((t) => ({ id: t.id, name: t.name }));
    }
    if (data.corrector_ids !== undefined && optimistic?.correctors) {
      updated.correctors = optimistic.correctors.map((c) => ({ id: c.id, name: c.name }));
    }
    if (data.theme !== undefined) updated.theme = data.theme;
    if (data.rights !== undefined) updated.rights = data.rights;
    if (data.edition_id !== undefined) {
      updated.edition_id = data.edition_id;
      if (optimistic?.edition) {
        updated.edition_name = optimistic.edition.edition_name;
        updated.edition_department_id = optimistic.edition.edition_department_id ?? undefined;
        updated.edition_department_code = optimistic.edition.edition_department_code ?? undefined;
      } else if (data.edition_id === null) {
        updated.edition_name = null;
        updated.edition_department_id = null;
        updated.edition_department_code = null;
      }
    }
    if (data.editor_id !== undefined) {
      updated.editor_id = data.editor_id;
      if (optimistic?.editor) {
        updated.editor_name = optimistic.editor.editor_name;
        if (optimistic.editor.edition_name !== undefined) updated.edition_name = optimistic.editor.edition_name;
        if (optimistic.editor.edition_department_id !== undefined) updated.edition_department_id = optimistic.editor.edition_department_id;
        if (optimistic.editor.edition_department_code !== undefined) updated.edition_department_code = optimistic.editor.edition_department_code;
      } else if (data.editor_id === null) {
        updated.editor_name = null;
      }
    }
    return updated;
  });
}

function mergeProjectFromServerInList(prev: Project[] | undefined, data: Project): Project[] | undefined {
  if (!prev) return prev;
  return prev.map((p) =>
    p.id === data.id ? { ...data, sessions: data.sessions ?? p.sessions ?? [] } : p
  );
}

export type EditableFieldKey =
  | 'name' | 'author' | 'readers' | 'theme' | 'rights' | 'author_sheets' | 'tracks_count' | 'size' | 'pages' | 'time' | 'deadline' | 'review_check_type' | 'review_deadline' | 'corrections_number' | 'no_edits' | 'paper_book_plan'
  | 'performers' | 'customers' | 'book_data'
  | 'producer' | 'techeditor' | 'corrector' | 'studio'
  | 'editor' | 'edition' | 'department'
  | 'asta' | 'ase' | 'rin'
  | 'combo';

interface EditableCellProps {
  project: Project;
  fieldKey: EditableFieldKey;
  children: React.ReactNode;
  /** Для комбо-ячеек — список активных дочерних столбцов. Определяет, какие поля показывать и сохранять. */
  comboChildIds?: string[];
  /** Переопределяет заголовок поповера (для пользовательских комбо). */
  label?: string;
  onUpdate?: (projectId: string, data: UpdateProjectData) => void;
}

function getFieldLabel(key: EditableFieldKey): string {
  const labels: Record<EditableFieldKey, string> = {
    name: 'Название',
    author: 'Автор',
    readers: 'Чтец',
    performers: 'Исполнители',
    customers: 'Заказчики',
    theme: 'Тематика',
    rights: 'Права',
    author_sheets: 'Авторские листы',
    tracks_count: 'Количество треков',
    size: 'Размер',
    pages: 'Страницы',
    time: 'Время',
    deadline: 'Дедлайн',
    review_check_type: 'Тип проверки',
    review_deadline: 'Дедлайн проверки',
    corrections_number: 'Кол-во правок',
    no_edits: 'Без правок',
    book_data: 'Данные',
    paper_book_plan: 'План выхода бумажной книги',
    producer: 'Продюсер',
    techeditor: 'Техредактор',
    corrector: 'Корректор',
    studio: 'Контрагенты',
    editor: 'Редактор',
    edition: 'Редакция',
    department: 'Редакция',
    asta: 'ASTA',
    ase: 'ASE',
    rin: 'РИН',
    combo: 'Комбо',
  };
  return labels[key];
}

/** Ширина попапа для одного столбца — единая с прежней логикой одиночных полей; для комбо берётся max по дочерним id */
function getPopoverWidthForColumnId(columnId: string): number {
  switch (columnId) {
    case 'name':
    case 'theme':
      return 420;
    case 'readers':
      return 660;
    case 'corrections_number':
      return 240;
    case 'author':
      return 340;
    case 'studio':
      return 500;
    case 'producer':
    case 'techeditor':
    case 'corrector':
      return 340;
    case 'editor':
    case 'edition':
    case 'department':
      return 320;
    case 'time':
      return 220;
    case 'ase':
      return 280;
    case 'book_data':
      return 320;
    case 'paper_book_plan':
      return 320;
    default:
      return 260;
  }
}

export function EditableCell({ project, fieldKey, children, comboChildIds, label, onUpdate }: EditableCellProps) {
  const cellCommentShell = useCellCommentShellOptional();
  const openProjectDetailsFromRow = useProjectRowOpenDetails();
  const { showSuccess, showError } = useToast();
  const { capabilities } = useAuth();
  const hidePrices = !capabilities.prices;
  const queryClient = useQueryClient();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const historyColumnId = useMemo(() => resolveHistoryColumnId(fieldKey), [fieldKey]);
  const historyColumnIds = useMemo(
    () => resolveHistoryColumnIdsForCombo(fieldKey, comboChildIds),
    [fieldKey, comboChildIds]
  );
  const historyTitle = label ?? getFieldLabel(fieldKey);
  const [nameNotePopoverOpen, setNameNotePopoverOpen] = useState(false);
  const [nameNoteDraft, setNameNoteDraft] = useState('');
  const [nameNoteError, setNameNoteError] = useState('');
  const cellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const nameNotePopoverRef = useRef<HTMLDivElement>(null);
  const authorComboRef = useRef<HTMLDivElement>(null);
  const producerComboRef = useRef<HTMLDivElement>(null);
  const techeditorComboRef = useRef<HTMLDivElement>(null);
  const correctorComboRef = useRef<HTMLDivElement>(null);
  const departmentComboRef = useRef<HTMLDivElement>(null);
  const editionComboRef = useRef<HTMLDivElement>(null);
  const editorComboRef = useRef<HTMLDivElement>(null);
  const positionAnchorRef = useRef<HTMLDivElement | null>(null);
  if (popoverOpen && cellRef.current) positionAnchorRef.current = cellRef.current;
  const triggerRef = useRef<HTMLDivElement | null>(null);
  triggerRef.current = popoverOpen ? cellRef.current : positionAnchorRef.current;

  const [error, setError] = useState('');
  const skipNextOpenRef = useRef(false);

  const { shouldRender, contentClasses, requestClose: requestOverlayClose, isExiting } = useOverlayAnimation(
    popoverOpen,
    useCallback(() => {
      setPopoverOpen(false);
      setError('');
    }, []),
    true
  );

  const {
    shouldRender: nameNoteShouldRender,
    contentClasses: nameNoteContentClasses,
    requestClose: requestNameNoteClose,
    isExiting: nameNoteIsExiting,
  } = useOverlayAnimation(
    nameNotePopoverOpen,
    useCallback(() => {
      setNameNotePopoverOpen(false);
      setNameNoteError('');
    }, []),
    true
  );

  const requestClose = useCallback(
    (e?: MouseEvent) => {
      if (e?.target && cellRef.current?.contains(e.target as Node)) {
        skipNextOpenRef.current = true;
      }
      requestOverlayClose();
    },
    [requestOverlayClose]
  );

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    dependencies: [fieldKey],
  });

  usePopoverPositioning({
    triggerRef: cellRef as React.RefObject<HTMLElement | null>,
    popoverRef: nameNotePopoverRef,
    isOpen: nameNoteShouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1001,
    dependencies: [fieldKey, nameNotePopoverOpen],
  });

  type MutationVars = {
    id: string;
    data: UpdateProjectData;
    optimistic?: EditableCellOptimistic;
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: MutationVars) =>
      api.projects.update(id, hidePrices ? omitPriceKeys(data) : data),
    onMutate: async ({ id, data, optimistic }: MutationVars) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      const patch = (prev: Project[] | undefined) => applyOptimisticProjectPatch(prev, id, data, optimistic);
      patchProjectListCaches(queryClient, patch);
      markProjectUpdatedByMutation(id);
      return rollback;
    },
    onError: (err: Error, vars, context: ProjectListRollbackContext | undefined) => {
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg);
      void rollbackProjectsAndReconcile(queryClient, context).catch(() => {});
      if (isNameNoteOnlyPatch(vars?.data)) {
        setNameNoteError(err.message);
        return;
      }
      setError(err.message);
    },
    onSuccess: (data, vars) => {
      showSuccess('Данные сохранены');
      const { id } = vars;
      setLastUndoRedoProjectId(id);
      const merge = (prev: Project[] | undefined) => mergeProjectFromServerInList(prev, data);
      patchProjectListCaches(queryClient, merge);
      invalidateProjectHistory(queryClient, id, historyColumnId);
      void refreshUndoRedoAvailability(queryClient, id).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      queryClient.invalidateQueries({ queryKey: ['readers'] });
      onUpdate?.(id, {});
      if (isNameNoteOnlyPatch(vars?.data)) {
        setNameNotePopoverOpen(false);
        setNameNoteError('');
      }
      requestOverlayClose();
    },
  });

  const handleContractorBudgetToggle = useCallback(
    (rowKey: string, nextIncluded: boolean) => {
      const rowKeyNorm = String(rowKey);
      const current = projectContractorsToEntries((project.project_contracts ?? project.contractors ?? []));
      const nextEntries = current.map((e) =>
        String(e.project_contractor_id ?? '') === rowKeyNorm ? { ...e, is_contractor_included_in_budget: nextIncluded } : e
      );
      updateMutation.mutate({
        id: project.id,
        data: { project_contracts: entriesToInputs(nextEntries) },
        optimistic: { contractorEntries: nextEntries },
      });
    },
    [project.id, project.project_contracts, project.contractors, updateMutation]
  );

  const [name, setName] = useState(project.name);
  const [authors, setAuthors] = useState<AuthorValueItem[]>(
    (project.authors ?? []).map((a) => ({ id: a.id, name: a.name }))
  );
  const [readers, setReaders] = useState<ReaderEntryItem[]>(
    projectReadersToEntries(project.readers ?? [])
  );
  const [contractorEntries, setContractorEntries] = useState<ContractorEntryItem[]>(
    projectContractorsToEntries((project.project_contracts ?? project.contractors ?? []))
  );
  const [producers, setProducers] = useState<ProducerValueItem[]>(
    (project.producers ?? []).map((p) => ({ id: p.id, name: p.name }))
  );
  const [techeditors, setTecheditors] = useState<TecheditorValueItem[]>(
    (project.techeditors ?? []).map((t) => ({ id: t.id, name: t.name }))
  );
  const [correctors, setCorrectors] = useState<CorrectorValueItem[]>(
    (project.correctors ?? []).map((c) => ({ id: c.id, name: c.name }))
  );
  const [departmentValue, setDepartmentValue] = useState<DepartmentValueItem | null>(
    project.edition_department_id ? { id: project.edition_department_id, code: project.edition_department_code ?? '' } : null
  );
  const [editionValue, setEditionValue] = useState<EditionValueItem | null>(
    project.edition_id ? { id: project.edition_id, name: project.edition_name ?? '' } : null
  );
  const [editorValue, setEditorValue] = useState<EditorValueItem | null>(
    project.editor_id
      ? {
          id: project.editor_id,
          name: project.editor_name ?? '',
          edition_id: project.edition_id ?? undefined,
          edition_name: project.edition_name ?? undefined,
          department_id: project.edition_department_id ?? undefined,
          department_code: project.edition_department_code ?? undefined,
        }
      : null
  );

  const { onDepartmentChange, onEditionChange, onEditorChange } = useEditionEditorDepartmentHandlers(
    setDepartmentValue,
    setEditionValue,
    setEditorValue
  );

  const [theme, setTheme] = useState(project.theme ?? '');
  const [rights, setRights] = useState(project.rights ?? '');
  const [pages, setPages] = useState(String(project.pages ?? 0));
  const [authorSheets, setAuthorSheets] = useState(
    project.author_sheets != null ? String(project.author_sheets) : ''
  );
  const [tracksCount, setTracksCount] = useState(
    project.tracks_count != null ? String(project.tracks_count) : ''
  );
  const [correctionsCount, setCorrectionsCount] = useState(
    project.corrections_number != null ? String(project.corrections_number) : ''
  );
  const [sizeValue, setSizeValue] = useState(
    project.size != null ? String(project.size) : ''
  );
  const [time, setTime] = useState(project.time ?? '');
  const [deadline, setDeadline] = useState(
    project.deadline ? (() => {
      try {
        const d = parse(project.deadline, 'yyyy-MM-dd', new Date());
        return isValid(d) ? format(d, 'dd.MM.yyyy') : project.deadline;
      } catch {
        return project.deadline;
      }
    })() : ''
  );
  const [asta, setAsta] = useState(project.asta ?? '');
  const [ase, setAse] = useState(project.ase ?? '');
  const [rin, setRin] = useState(project.rin ?? '');
  const [reviewCheckType, setReviewCheckType] = useState(project.review_check_type?.trim() ?? '');
  const [reviewDeadline, setReviewDeadline] = useState(
    project.review_deadline ? (() => {
      try {
        const d = parse(project.review_deadline, 'yyyy-MM-dd', new Date());
        return isValid(d) ? format(d, 'dd.MM.yyyy') : project.review_deadline;
      } catch {
        return project.review_deadline;
      }
    })() : ''
  );
  const [noEdits, setNoEdits] = useState(() => !!project.no_edits);
  const [paperBookPlan, setPaperBookPlan] = useState(
    project.paper_book_plan ? (() => {
      try {
        const d = parse(project.paper_book_plan, 'yyyy-MM-dd', new Date());
        return isValid(d) ? format(d, 'dd.MM.yyyy') : project.paper_book_plan;
      } catch {
        return project.paper_book_plan;
      }
    })() : ''
  );
  const [isBacklog, setIsBacklog] = useState(project.is_backlog ?? false);

  useEffect(() => {
    if (popoverOpen) {
      setName(project.name);
      setAuthors((project.authors ?? []).map((a) => ({ id: a.id, name: a.name })));
      setReaders(projectReadersToEntries(project.readers ?? []));
      setContractorEntries(projectContractorsToEntries((project.project_contracts ?? project.contractors ?? [])));
      setProducers((project.producers ?? []).map((p) => ({ id: p.id, name: p.name })));
      setTecheditors((project.techeditors ?? []).map((t) => ({ id: t.id, name: t.name })));
      setCorrectors((project.correctors ?? []).map((c) => ({ id: c.id, name: c.name })));
      setDepartmentValue(project.edition_department_id ? { id: project.edition_department_id, code: project.edition_department_code ?? '' } : null);
      setEditionValue(project.edition_id ? { id: project.edition_id, name: project.edition_name ?? '' } : null);
      setEditorValue(
        project.editor_id
          ? {
              id: project.editor_id,
              name: project.editor_name ?? '',
              edition_id: project.edition_id ?? undefined,
              edition_name: project.edition_name ?? undefined,
              department_id: project.edition_department_id ?? undefined,
              department_code: project.edition_department_code ?? undefined,
            }
          : null
      );
      setTheme(project.theme ?? '');
      setRights(project.rights ?? '');
      setPages(String(project.pages ?? 0));
      setAuthorSheets(project.author_sheets != null ? String(project.author_sheets) : '');
      setTracksCount(project.tracks_count != null ? String(project.tracks_count) : '');
      setCorrectionsCount(project.corrections_number != null ? String(project.corrections_number) : '');
      setSizeValue(project.size != null ? String(project.size) : '');
      setTime(project.time ?? '');
      setDeadline(
        project.deadline ? (() => {
          try {
            const d = parse(project.deadline, 'yyyy-MM-dd', new Date());
            return isValid(d) ? format(d, 'dd.MM.yyyy') : project.deadline;
          } catch {
            return project.deadline;
          }
        })() : ''
      );
      setAsta(project.asta ?? '');
      setAse(project.ase ?? '');
      setRin(project.rin ?? '');
      setReviewCheckType(project.review_check_type?.trim() ?? '');
      setReviewDeadline(
        project.review_deadline ? (() => {
          try {
            const d = parse(project.review_deadline, 'yyyy-MM-dd', new Date());
            return isValid(d) ? format(d, 'dd.MM.yyyy') : project.review_deadline;
          } catch {
            return project.review_deadline;
          }
        })() : ''
      );
      setNoEdits(!!project.no_edits);
      setPaperBookPlan(
        project.paper_book_plan ? (() => {
          try {
            const d = parse(project.paper_book_plan, 'yyyy-MM-dd', new Date());
            return isValid(d) ? format(d, 'dd.MM.yyyy') : project.paper_book_plan;
          } catch {
            return project.paper_book_plan;
          }
        })() : ''
      );
      setIsBacklog(project.is_backlog ?? false);
      setError('');
    }
  }, [popoverOpen, project]);

  const openEditPopover = useCallback(() => {
    if (skipNextOpenRef.current) {
      skipNextOpenRef.current = false;
      return;
    }
    setPopoverOpen(true);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  }, []);

  const handleEditFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    openEditPopover();
  }, [openEditPopover]);

  const openNameNotePopover = useCallback(() => {
    setNameNoteDraft(nameNoteSingleLine(project.name_note));
    setNameNoteError('');
    setNameNotePopoverOpen(true);
  }, [project.name_note]);

  const handleSaveNameNote = useCallback(() => {
    setNameNoteError('');
    const trimmed = nameNoteSingleLine(nameNoteDraft);
    if (!trimmed) {
      setNameNoteError('Введите текст заметки');
      return;
    }
    if (trimmed.length > MAX_NAME_NOTE_LEN) {
      setNameNoteError(`Не более ${MAX_NAME_NOTE_LEN} символов`);
      return;
    }
    updateMutation.mutate({ id: project.id, data: { name_note: trimmed } });
  }, [nameNoteDraft, project.id, updateMutation]);

  const handleRemoveNameNoteConfirmed = useCallback(() => {
    if (!window.confirm('Удалить заметку к названию проекта?')) return;
    updateMutation.mutate({ id: project.id, data: { name_note: null } });
  }, [project.id, updateMutation]);

  const highlighted = !!project.is_project_highlighted;

  const contextMenuItems = useMemo(() => {
    const items: ContextMenuItem[] = [{ label: 'Редактировать', onClick: handleEditFromMenu }];
    items.push({
      label: 'Посмотреть изменения',
      icon: <HistoryIcon size={18} />,
      onClick: () => {
        setHistoryAnchorEl(cellRef.current);
        setContextMenuOpen(false);
        setHistoryOpen(true);
      },
    });
    if (openProjectDetailsFromRow) {
      items.push({
        label: 'Информация о проекте',
        icon: <InfoIcon size={18} />,
        onClick: () => {
          setContextMenuOpen(false);
          openProjectDetailsFromRow(cellRef.current);
        },
      });
    }
    if (cellCommentShell) items.push(cellCommentShell.addCommentMenuItem);
    items.push({
      label: highlighted ? 'Снять выделение' : 'Выделить проект',
      icon: highlighted ? (
        <i className="fa-regular fa-bookmark editable-cell__context-menu-fa" aria-hidden />
      ) : (
        <BookmarkIcon size={18} />
      ),
      onClick: () => {
        setContextMenuOpen(false);
        updateMutation.mutate({ id: project.id, data: { is_project_highlighted: !highlighted } });
      },
    });
    if (fieldKey === 'name') {
      const hasNote = !!nameNoteSingleLine(project.name_note);
      if (hasNote) {
        items.push({
          label: 'Изменить заметку',
          icon: <i className="fa-solid fa-file-pen editable-cell__context-menu-fa" aria-hidden />,
          onClick: () => {
            setContextMenuOpen(false);
            openNameNotePopover();
          },
        });
        items.push({
          label: 'Удалить заметку',
          onClick: () => {
            setContextMenuOpen(false);
            handleRemoveNameNoteConfirmed();
          },
          danger: true,
        });
      } else {
        items.push({
          label: 'Добавить заметку',
          icon: <i className="fa-solid fa-file editable-cell__context-menu-fa" aria-hidden />,
          onClick: () => {
            setContextMenuOpen(false);
            openNameNotePopover();
          },
        });
      }
    }
    return items;
  }, [
    cellCommentShell,
    fieldKey,
    handleEditFromMenu,
    handleRemoveNameNoteConfirmed,
    highlighted,
    openNameNotePopover,
    openProjectDetailsFromRow,
    project.id,
    project.name_note,
    updateMutation,
  ]);

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [
      authorComboRef,
      producerComboRef,
      techeditorComboRef,
      correctorComboRef,
      departmentComboRef,
      editionComboRef,
      editorComboRef,
      nameNotePopoverRef,
    ],
    excludeSelector: '[data-combo-dropdown], .modal-overlay',
    enabled: shouldRender,
    capture: false,
  });

  useClickOutside(nameNotePopoverRef, requestNameNoteClose as (e: MouseEvent) => void, {
    excludeRefs: [cellRef, popoverRef],
    excludeSelector: '[data-combo-dropdown]',
    enabled: nameNoteShouldRender,
    capture: false,
  });

  const handleSave = useCallback(() => {
    setError('');
    const id = project.id;

    const buildDataForColumns = (active: string[]): { data: UpdateProjectData; optimistic: MutationVars['optimistic'] } => {
      const data: UpdateProjectData = {};
      const optimistic: MutationVars['optimistic'] = {};
      if (active.includes('author')) { data.author_ids = authors.map((a) => a.id); optimistic.authors = authors; }
      if (active.includes('readers')) {
        data.reader_entries = entriesToReaderInputs(readers);
        optimistic.readers = readers;
      }
      if (active.includes('producer')) { data.producer_ids = producers.map((p) => p.id); optimistic.producers = producers; }
      if (active.includes('techeditor')) { data.techeditor_ids = techeditors.map((t) => t.id); optimistic.techeditors = techeditors; }
      if (active.includes('corrector')) { data.corrector_ids = correctors.map((c) => c.id); optimistic.correctors = correctors; }
      if (active.includes('studio')) { data.project_contracts = entriesToInputs(contractorEntries); optimistic.contractorEntries = contractorEntries; }
      if (active.includes('editor')) {
        data.editor_id = editorValue?.id ?? null;
        if (editorValue?.edition_id) {
          data.edition_id = editorValue.edition_id;
        }
        optimistic.editor = editorValue
          ? {
              editor_name: editorValue.name,
              edition_name: editorValue.edition_name ?? null,
              edition_department_id: editorValue.department_id ?? null,
              edition_department_code: editorValue.department_code ?? null,
            }
          : undefined;
      }
      if (active.includes('edition') || active.includes('department')) {
        data.edition_id = editionValue?.id ?? null;
        optimistic.edition = editionValue
          ? {
              edition_name: editionValue.name,
              edition_department_id: editionValue.department_id ?? departmentValue?.id ?? null,
              edition_department_code: editionValue.department_code ?? departmentValue?.code ?? null,
            }
          : undefined;
      }
      if (active.includes('theme')) data.theme = theme.trim() || null;
      if (active.includes('rights')) data.rights = rights.trim() || null;
      if (active.includes('pages')) {
        const pagesNum = pages ? parseInt(pages.replace(',', '.'), 10) : 0;
        data.pages = pagesNum;
      }
      if (active.includes('author_sheets')) {
        data.author_sheets = authorSheets ? parseFloat(authorSheets.replace(',', '.')) : null;
      }
      if (active.includes('tracks_count')) {
        const n = tracksCount ? parseInt(tracksCount.replace(/[^\d]/g, ''), 10) : null;
        data.tracks_count = n != null && !isNaN(n) ? n : null;
      }
      if (active.includes('corrections_number')) {
        const n = correctionsCount ? parseInt(correctionsCount.replace(/[^\d]/g, ''), 10) : null;
        data.corrections_number = n != null && !isNaN(n) ? n : null;
      }
      if (active.includes('size')) {
        data.size = sizeValue ? parseFloat(sizeValue.replace(',', '.')) : null;
      }
      if (active.includes('time')) {
        data.time = time.match(/^\d{2}:\d{2}:\d{2}$/) ? time : null;
      }
      if (active.includes('deadline')) {
        data.deadline = deadline.trim() ? (() => { try { const d = parse(deadline.trim(), 'dd.MM.yyyy', new Date()); return isValid(d) ? format(d, 'yyyy-MM-dd') : null; } catch { return null; } })() : null;
      }
      if (active.includes('review_check_type')) {
        data.review_check_type = reviewCheckType.trim() ? reviewCheckType.trim() : null;
      }
      if (active.includes('review_deadline')) {
        data.review_deadline = reviewDeadline.trim() ? (() => { try { const d = parse(reviewDeadline.trim(), 'dd.MM.yyyy', new Date()); return isValid(d) ? format(d, 'yyyy-MM-dd') : null; } catch { return null; } })() : null;
      }
      if (active.includes('no_edits')) {
        data.no_edits = noEdits;
      }
      if (active.includes('paper_book_plan')) {
        data.paper_book_plan = paperBookPlan.trim() ? (() => { try { const d = parse(paperBookPlan.trim(), 'dd.MM.yyyy', new Date()); return isValid(d) ? format(d, 'yyyy-MM-dd') : null; } catch { return null; } })() : null;
        data.is_backlog = isBacklog;
      }
      if (active.includes('asta')) data.asta = asta.trim() || null;
      if (active.includes('ase')) data.ase = ase.trim() || null;
      if (active.includes('rin')) data.rin = rin.trim() || null;
      return { data, optimistic };
    };

    switch (fieldKey) {
      case 'name': {
        const trimmed = name.trim();
        if (!trimmed) {
          setError('Укажите название');
          return;
        }
        updateMutation.mutate({ id, data: { name: trimmed } });
        break;
      }
      case 'author': {
        updateMutation.mutate({
          id,
          data: { author_ids: authors.map((a) => a.id) },
          optimistic: { authors },
        });
        break;
      }
      case 'readers': {
        for (const r of readers) {
          if (r.id && !isValidReaderTimeInput(r.time_input)) {
            setError('Введите время по чтецу в формате чч:мм:сс или оставьте пустым');
            return;
          }
        }
        updateMutation.mutate({
          id,
          data: { reader_entries: entriesToReaderInputs(readers) },
          optimistic: { readers },
        });
        break;
      }
      case 'combo': {
        const active = comboChildIds ?? [];
        if (active.includes('readers')) {
          for (const r of readers) {
            if (r.id && !isValidReaderTimeInput(r.time_input)) {
              setError('Введите время по чтецу в формате чч:мм:сс или оставьте пустым');
              return;
            }
          }
        }
        if (active.length > 0) {
          const { data, optimistic } = buildDataForColumns(active);
          updateMutation.mutate({ id, data, optimistic });
        }
        break;
      }
      case 'producer': {
        updateMutation.mutate({
          id,
          data: { producer_ids: producers.map((p) => p.id) },
          optimistic: { producers },
        });
        break;
      }
      case 'techeditor': {
        updateMutation.mutate({
          id,
          data: { techeditor_ids: techeditors.map((t) => t.id) },
          optimistic: { techeditors },
        });
        break;
      }
      case 'corrector': {
        updateMutation.mutate({
          id,
          data: { corrector_ids: correctors.map((c) => c.id) },
          optimistic: { correctors },
        });
        break;
      }
      case 'studio': {
        updateMutation.mutate({
          id,
          data: { project_contracts: entriesToInputs(contractorEntries) },
          optimistic: { contractorEntries },
        });
        break;
      }
      case 'editor': {
        const editorData: UpdateProjectData = { editor_id: editorValue?.id ?? null };
        if (editorValue?.edition_id) {
          editorData.edition_id = editorValue.edition_id;
        }
        updateMutation.mutate({
          id,
          data: editorData,
          optimistic: editorValue
            ? {
                editor: {
                  editor_name: editorValue.name,
                  edition_name: editorValue.edition_name ?? null,
                  edition_department_id: editorValue.department_id ?? null,
                  edition_department_code: editorValue.department_code ?? null,
                },
              }
            : undefined,
        });
        break;
      }
      case 'edition': {
        updateMutation.mutate({
          id,
          data: { edition_id: editionValue?.id ?? null },
          optimistic: editionValue
            ? {
                edition: {
                  edition_name: editionValue.name,
                  edition_department_id: editionValue.department_id ?? departmentValue?.id ?? null,
                  edition_department_code: editionValue.department_code ?? departmentValue?.code ?? null,
                },
              }
            : undefined,
        });
        break;
      }
      case 'department': {
        updateMutation.mutate({
          id,
          data: { edition_id: editionValue?.id ?? null },
          optimistic: editionValue
            ? {
                edition: {
                  edition_name: editionValue.name,
                  edition_department_id: editionValue.department_id ?? departmentValue?.id ?? null,
                  edition_department_code: editionValue.department_code ?? departmentValue?.code ?? null,
                },
              }
            : undefined,
        });
        break;
      }
      case 'theme': {
        updateMutation.mutate({ id, data: { theme: theme.trim() || null } });
        break;
      }
      case 'rights': {
        updateMutation.mutate({ id, data: { rights: rights.trim() || null } });
        break;
      }
      case 'pages': {
        const pagesNum = pages ? parseInt(pages.replace(',', '.'), 10) : 0;
        if (pages && (isNaN(pagesNum) || pagesNum < 0)) {
          setError('Страницы должны быть числом ≥ 0');
          return;
        }
        updateMutation.mutate({ id, data: { pages: pagesNum } });
        break;
      }
      case 'author_sheets': {
        const val = authorSheets ? parseFloat(authorSheets.replace(',', '.')) : null;
        if (authorSheets && (val === null || isNaN(val) || val < 0)) {
          setError('Авторские листы должны быть числом ≥ 0');
          return;
        }
        updateMutation.mutate({ id, data: { author_sheets: val } });
        break;
      }
      case 'tracks_count': {
        const n = tracksCount ? parseInt(tracksCount.replace(/[^\d]/g, ''), 10) : null;
        if (tracksCount && (n == null || isNaN(n) || n < 0)) {
          setError('Количество треков должно быть целым числом ≥ 0');
          return;
        }
        updateMutation.mutate({ id, data: { tracks_count: n } });
        break;
      }
      case 'corrections_number': {
        const n = correctionsCount ? parseInt(correctionsCount.replace(/[^\d]/g, ''), 10) : null;
        if (correctionsCount && (n == null || isNaN(n) || n < 0)) {
          setError('Кол-во правок должно быть целым числом ≥ 0');
          return;
        }
        updateMutation.mutate({ id, data: { corrections_number: n } });
        break;
      }
      case 'size': {
        const val = sizeValue ? parseFloat(sizeValue.replace(',', '.')) : null;
        if (sizeValue && (val === null || isNaN(val) || val < 0)) {
          setError('Размер должен быть числом ≥ 0');
          return;
        }
        updateMutation.mutate({ id, data: { size: val } });
        break;
      }
      case 'time': {
        const timeVal = time.match(/^\d{2}:\d{2}:\d{2}$/) ? time : null;
        if (time && !timeVal) {
          setError('Введите время в формате чч:мм:сс');
          return;
        }
        updateMutation.mutate({ id, data: { time: timeVal } });
        break;
      }
      case 'deadline': {
        const deadlineForApi = deadline.trim()
          ? (() => {
              try {
                const d = parse(deadline.trim(), 'dd.MM.yyyy', new Date());
                return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
              } catch {
                return null;
              }
            })()
          : null;
        updateMutation.mutate({ id, data: { deadline: deadlineForApi } });
        break;
      }
      case 'review_check_type': {
        updateMutation.mutate({ id, data: { review_check_type: reviewCheckType.trim() ? reviewCheckType.trim() : null } });
        break;
      }
      case 'review_deadline': {
        const reviewDeadlineForApi = reviewDeadline.trim()
          ? (() => {
              try {
                const d = parse(reviewDeadline.trim(), 'dd.MM.yyyy', new Date());
                return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
              } catch {
                return null;
              }
            })()
          : null;
        updateMutation.mutate({ id, data: { review_deadline: reviewDeadlineForApi } });
        break;
      }
      case 'no_edits': {
        updateMutation.mutate({
          id,
          data: { no_edits: noEdits },
        });
        break;
      }
      case 'asta': {
        updateMutation.mutate({ id, data: { asta: asta.trim() || null } });
        break;
      }
      case 'ase': {
        updateMutation.mutate({ id, data: { ase: ase.trim() || null } });
        break;
      }
      case 'rin': {
        updateMutation.mutate({ id, data: { rin: rin.trim() || null } });
        break;
      }
      case 'paper_book_plan': {
        const paperBookPlanForApi = paperBookPlan.trim()
          ? (() => {
              try {
                const d = parse(paperBookPlan.trim(), 'dd.MM.yyyy', new Date());
                return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
              } catch {
                return null;
              }
            })()
          : null;
        updateMutation.mutate({ id, data: { paper_book_plan: paperBookPlanForApi, is_backlog: isBacklog } });
        break;
      }
    }
  }, [
    project.id,
    fieldKey,
    comboChildIds,
    name,
    authors,
    readers,
    contractorEntries,
    producers,
    techeditors,
    correctors,
    editionValue,
    editorValue,
    departmentValue,
    theme,
    rights,
    pages,
    authorSheets,
    tracksCount,
    correctionsCount,
    sizeValue,
    time,
    deadline,
    asta,
    ase,
    rin,
    reviewCheckType,
    reviewDeadline,
    noEdits,
    paperBookPlan,
    isBacklog,
    updateMutation,
  ]);

  const renderFormContent = () => {
    /** Универсальный рендер одного поля по ID столбца. autoFocus только для первого поля. */
    const renderField = (columnId: string, isFirst: boolean): React.ReactNode => {
      switch (columnId) {
        case 'name':
          return (
            <Textarea value={name} onChange={(e) => setName(e.target.value)} placeholder="Название проекта"
              fullWidth autoFocus={isFirst} minRows={1} maxRows={6} />
          );
        case 'author':
          return <AuthorCombo value={authors} onChange={setAuthors} placeholder="Автор" containerRef={authorComboRef} />;
        case 'readers':
          return <ReaderPriceList value={readers} onChange={setReaders} showReaderCorrections hidePrices={hidePrices} />;
        case 'producer':
          return <ProducerCombo value={producers} onChange={setProducers} placeholder="Продюсер" containerRef={producerComboRef} />;
        case 'techeditor':
          return <TecheditorCombo value={techeditors} onChange={setTecheditors} placeholder="Выберите техредактора" containerRef={techeditorComboRef} />;
        case 'corrector':
          return <CorrectorCombo value={correctors} onChange={setCorrectors} placeholder="Корректор" containerRef={correctorComboRef} />;
        case 'studio':
          return <ContractorServiceList value={contractorEntries} onChange={setContractorEntries} hidePrices={hidePrices} />;
        case 'editor':
          return (
            <EditorCombo
              value={editorValue}
              onChange={onEditorChange}
              departmentId={departmentValue?.id ?? null}
              placeholder="Редактор"
              containerRef={editorComboRef}
            />
          );
        case 'edition':
          return (
            <EditionCombo
              value={editionValue}
              onChange={onEditionChange}
              departmentId={departmentValue?.id ?? null}
              placeholder="Редакция"
              containerRef={editionComboRef}
              onCreateError={(e) => setError(e.message)}
            />
          );
        case 'department':
          return comboChildIds?.includes('edition') ? (
            <DepartmentCombo
              value={departmentValue}
              onChange={onDepartmentChange}
              placeholder="Подразделение"
              containerRef={departmentComboRef}
              onCreateError={(e) => setError(e.message)}
            />
          ) : (
            <EditionCombo
              value={editionValue}
              onChange={onEditionChange}
              departmentId={departmentValue?.id ?? null}
              placeholder="Редакция"
              containerRef={editionComboRef}
              onCreateError={(e) => setError(e.message)}
            />
          );
        case 'theme':
          return (
            <Textarea
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Тематика"
              fullWidth
              autoFocus={isFirst}
              minRows={1}
              maxRows={6}
            />
          );
        case 'rights':
          return <Input value={rights} onChange={(e) => setRights(e.target.value)} placeholder="Права" fullWidth autoFocus={isFirst} />;
        case 'pages':
          return <Input value={pages} onChange={(e) => setPages(e.target.value.replace(/[^\d]/g, ''))} placeholder="Страницы" fullWidth autoFocus={isFirst} />;
        case 'author_sheets':
          return (
            <Input
              value={authorSheets}
              onChange={(e) => {
                const v = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
                const parts = v.split('.');
                if (parts.length <= 1) setAuthorSheets(v);
                else setAuthorSheets(parts[0] + '.' + parts.slice(1).join(''));
              }}
              placeholder="Авторские листы"
              fullWidth
              autoFocus={isFirst}
            />
          );
        case 'tracks_count':
          return <Input value={tracksCount} onChange={(e) => setTracksCount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Количество треков" fullWidth autoFocus={isFirst} />;
        case 'corrections_number':
          return (
            <Input
              value={correctionsCount}
              onChange={(e) => setCorrectionsCount(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="Кол-во правок"
              fullWidth
              autoFocus={isFirst}
            />
          );
        case 'size':
          return (
            <Input
              value={sizeValue}
              onChange={(e) => {
                const v = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
                const parts = v.split('.');
                if (parts.length <= 1) setSizeValue(v);
                else setSizeValue(parts[0] + '.' + parts.slice(1).join(''));
              }}
              placeholder="Размер"
              fullWidth
              autoFocus={isFirst}
            />
          );
        case 'time':
          return (
            <TimeInput
              value={time}
              onChange={setTime}
              placeholder="00:00:00"
              fullWidth
              autoFocus={isFirst}
            />
          );
        case 'deadline':
          return <DateAutofillInput value={deadline} onChange={setDeadline} placeholder="Дедлайн" fullWidth />;
        case 'review_check_type':
          return (
            <SelectDropdown
              fullWidth
              value={reviewCheckType}
              onChange={setReviewCheckType}
              placeholder={REVIEW_CHECK_TYPE_PLACEHOLDER}
              options={[
                { value: '', label: REVIEW_NOT_SPECIFIED_LABEL },
                ...REVIEW_CHECK_TYPES.map((k) => ({ value: k, label: REVIEW_CHECK_TYPE_LABELS[k] })),
              ]}
              renderValue={() => {
                const k = reviewCheckType.trim();
                if (!k) {
                  return (
                    <span className="select-dropdown__value select-dropdown__placeholder">{REVIEW_CHECK_TYPE_PLACEHOLDER}</span>
                  );
                }
                const lbl = REVIEW_CHECK_TYPE_LABELS[k as ReviewCheckType];
                return <span className="select-dropdown__value">{lbl ?? k}</span>;
              }}
            />
          );
        case 'review_deadline':
          return <DateAutofillInput value={reviewDeadline} onChange={setReviewDeadline} placeholder="Дедлайн проверки" fullWidth />;
        case 'no_edits':
          return (
            <Switch checked={noEdits} onCheckedChange={setNoEdits} label="Без правок" />
          );
        case 'paper_book_plan':
          return (
            <PaperBookPlanBacklogCombo
              value={paperBookPlan}
              onChange={setPaperBookPlan}
              isBacklog={isBacklog}
              onBacklogChange={setIsBacklog}
            />
          );
        case 'asta':
          return <Input value={asta} onChange={(e) => setAsta(e.target.value)} placeholder="ASTA" fullWidth autoFocus={isFirst} />;
        case 'ase':
          return (
            <Input
              value={ase}
              onChange={(e) => setAse(e.target.value)}
              placeholder="ASE"
              maxLength={32}
              fullWidth
              autoFocus={isFirst}
            />
          );
        case 'rin':
          return <Input value={rin} onChange={(e) => setRin(e.target.value)} placeholder="РИН" fullWidth autoFocus={isFirst} />;
        default:
          return null;
      }
    };

    /** Рендер списка полей по массиву ID столбцов. */
    const renderFields = (active: string[]) => (
      <>
        {active.map((id, idx) => {
          const field = renderField(id, idx === 0);
          return field ? <Fragment key={id}>{field}</Fragment> : null;
        })}
        {error && <span className="form__error" role="alert">{error}</span>}
      </>
    );

    switch (fieldKey) {
      case 'name':
        return (
          <>
            <Textarea value={name} onChange={(e) => setName(e.target.value)} placeholder="Название проекта"
              fullWidth autoFocus minRows={1} maxRows={6} error={error} />
          </>
        );
      case 'author':
        return (
          <>
            <AuthorCombo value={authors} onChange={setAuthors} placeholder="Автор" containerRef={authorComboRef} />
            {error && <span className="form__error" role="alert">{error}</span>}
          </>
        );
      case 'readers':
        return (
          <>
            <ReaderPriceList value={readers} onChange={setReaders} showReaderCorrections hidePrices={hidePrices} />
            {error && <span className="form__error" role="alert">{error}</span>}
          </>
        );
      case 'combo':
        return renderFields(comboChildIds ?? []);
      case 'producer':
        return (<> {renderField('producer', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'techeditor':
        return (<> {renderField('techeditor', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'corrector':
        return (<> {renderField('corrector', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'studio':
        return (<> {renderField('studio', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'editor':
        return (<> {renderField('editor', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'edition':
        return (<> {renderField('edition', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'department':
        return (<> {renderField('department', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'theme':
        return (
          <>
            <Textarea
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Тематика"
              fullWidth
              autoFocus
              minRows={1}
              maxRows={6}
              error={error}
            />
          </>
        );
      case 'rights':
        return (<> {renderField('rights', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'pages':
        return (<> {renderField('pages', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'author_sheets':
        return (<> {renderField('author_sheets', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'tracks_count':
        return (<> {renderField('tracks_count', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'corrections_number':
        return (<> {renderField('corrections_number', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'size':
        return (<> {renderField('size', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'time':
        return (<> {renderField('time', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'deadline':
        return (<> {renderField('deadline', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'review_check_type':
        return (<> {renderField('review_check_type', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'review_deadline':
        return (<> {renderField('review_deadline', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'no_edits':
        return (<> {renderField('no_edits', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'asta':
        return (<> {renderField('asta', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'ase':
        return (<> {renderField('ase', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'rin':
        return (<> {renderField('rin', true)} {error && <span className="form__error" role="alert">{error}</span>} </>);
      case 'paper_book_plan':
        return (
          <>
            <PaperBookPlanBacklogCombo
              value={paperBookPlan}
              onChange={setPaperBookPlan}
              isBacklog={isBacklog}
              onBacklogChange={setIsBacklog}
            />
            {error && <span className="form__error" role="alert">{error}</span>}
          </>
        );
      default:
        return null;
    }
  };

  const isPending = updateMutation.isPending;

  const renderedCellChildren = useMemo(() => {
    if (fieldKey === 'studio' && isContractorsCellLike(children)) {
      return cloneElement(children, {
        onBudgetToggle: handleContractorBudgetToggle,
        budgetSaving: isPending,
      });
    }
    return children;
  }, [fieldKey, children, handleContractorBudgetToggle, isPending]);

  const isPerformersCombo = fieldKey === 'performers' || (fieldKey === 'combo' && label === 'Исполнители');
  const popoverWidth = (() => {
    if (fieldKey === 'combo') {
      const ids = comboChildIds ?? [];
      return ids.length > 0 ? Math.max(...ids.map(getPopoverWidthForColumnId)) : 360;
    }
    if (fieldKey === 'performers' || fieldKey === 'customers') {
      return 420;
    }
    return getPopoverWidthForColumnId(fieldKey);
  })();
  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: isExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: React.CSSProperties = {
    width: popoverWidth,
    minWidth: popoverWidth,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  const nameNotePopoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: nameNoteIsExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const nameNotePopoverPanelStyle: React.CSSProperties = {
    width: 320,
    minWidth: 280,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  return (
    <>
    <div ref={cellRef} className="editable-cell" onContextMenu={handleContextMenu}>
      <div className="editable-cell__content">
        {fieldKey === 'name' ? (
          <div className="editable-cell__name-stack">
            {renderedCellChildren}
            {nameNoteSingleLine(project.name_note) ? (
              <div className="editable-cell__name-note-wrap">
                <div
                  className="editable-cell__name-note-hit"
                  role="button"
                  tabIndex={0}
                  onClick={openNameNotePopover}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openNameNotePopover();
                    }
                  }}
                  aria-label="Редактировать заметку к названию"
                >
                  <Badge variant="custom" bg="var(--gradient-error)" color="#fff">
                    <span className="editable-cell__name-note-label">{nameNoteSingleLine(project.name_note)}</span>
                  </Badge>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          renderedCellChildren
        )}
      </div>
      <ContextMenu
        isOpen={contextMenuOpen}
        onClose={() => setContextMenuOpen(false)}
        anchorPosition={contextMenuPos}
        items={contextMenuItems}
      />
      <ProjectChangeHistoryPopover
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryAnchorEl(null);
        }}
        anchorEl={historyAnchorEl ?? cellRef.current}
        anchorPlacement="below-cell"
        projectId={project.id}
        projectName={project.name}
        columnId={historyColumnId}
        columnIds={historyColumnIds}
        title={historyTitle}
      />
      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={popoverPanelStyle}
                className={`editable-cell-popover ${isPerformersCombo ? 'editable-cell-popover_performers' : ''} ${contentClasses}`}
              >
                <EditableCellPopoverHeader onClose={requestClose}>{label ?? getFieldLabel(fieldKey)}</EditableCellPopoverHeader>
                <OverlayScrollArea className="editable-cell-popover__form editable-cell-popover__form_has-overlay-scroll">
                  <div className="editable-cell-popover__form-stack">{renderFormContent()}</div>
                </OverlayScrollArea>
                <div className="editable-cell-popover__footer">
                  <Button type="button" variant="primary" onClick={handleSave} disabled={isPending}>
                    {isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
      {nameNoteShouldRender &&
        createPortal(
          <div ref={nameNotePopoverRef} style={nameNotePopoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={nameNotePopoverPanelStyle}
                className={`editable-cell-popover editable-cell-popover_name-note ${nameNoteContentClasses}`}
              >
                <EditableCellPopoverHeader onClose={requestNameNoteClose}>Заметка к названию</EditableCellPopoverHeader>
                <div className="editable-cell-popover__form">
                  <div className="editable-cell-popover__form-stack">
                    <Textarea
                      value={nameNoteDraft}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\r\n|\r|\n/g, ' ').slice(0, MAX_NAME_NOTE_LEN);
                        setNameNoteDraft(v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.preventDefault();
                      }}
                      placeholder="До 40 символов"
                      fullWidth
                      autoFocus
                      minRows={1}
                      maxRows={NAME_NOTE_TEXTAREA_MAX_ROWS}
                      error={nameNoteError}
                      characterCount={{ current: nameNoteDraft.length, max: MAX_NAME_NOTE_LEN }}
                    />
                  </div>
                </div>
                <div className="editable-cell-popover__footer">
                  {nameNoteSingleLine(project.name_note) ? (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleRemoveNameNoteConfirmed}
                      disabled={isPending}
                    >
                      Удалить
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSaveNameNote}
                    disabled={isPending}
                  >
                    {isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </div>
    </>
  );
}
